import { gql } from "@apollo/client";
import { message } from "antd";
import { TAuditChangeLog } from "change-events/change-event-polling";
import {
  CompanyParticipationType,
  ErrorMessages,
  EUserTypes,
  UserParticipationType
} from "constants/index";
import { ProjectContext, TProjectContext } from "context/ProjectProvider";
import { FeatureTypes } from "entity-app/constants";
import {
  MUTATION_UPDATE_FEATURE_USER_PARTICIPANT,
  UPDATE_FEATURE_BID_PACKAGE_BY_PK,
  UPDATE_FEATURE_INSTANCE_BY_PK,
  UPDATE_FEATURE_WORKFLOW_INSTANCE_MILESTONE,
  UPDATE_FEATURE_INSTANCE_SPEC_SECTION,
  UPDATE_FEATURE_COMPANY_PARTICIPANT,
  UPDATE_FEATURE_COMPANY_PARTICIPANT_WITH_EMPTY_USERS,
  UPDATE_FEATURE_SUBMITTALS,
  UPDATE_FEATURE_MATERIALS,
  UPDATE_FEATURE_SHIPPING_LOCATION,
  CREATE_DUPLICATE_OF_FEATURE_INSTANCE,
  UPDATE_FEATURE_INSTANCES
} from "entity-app/graphQL/ciq-feature-mutations";
import { FEATURE_INSTANCE_DETAILS_BY_PK } from "entity-app/graphQL/ciq-feature-queries";
import { TFetureDetailUpdateAction } from "entity-app/models";
import { getGQLQueryData } from "entity-app/services";
import { APPLY_WORKFLOW_MUTATION } from "entity-app/shared-components/entity-dateblock/queries/mutation";
import { hasTheFeature } from "entity-app/utils/utils";
import { useCIQMutation } from "hooks/ciq-gql-hooks";
import { useProjectParticipants } from "hooks/project-participants";
import { useCallback, useContext, useEffect, useRef, useState } from "react";
import { isProjectInIntegrationMode, matchUserProjectRole } from "utils/utils";

export const useManageFeatureInstanceDetails = (
  featureId: string,
  featureType: FeatureTypes
): {
  featureInstanceData: any;
  actions: TFetureDetailUpdateAction;
} => {
  const {
    gqlClientForProject,
    tokenRetrievalState,
    eventLogs,
    projectDetails,
    tokenContents
  }: TProjectContext = useContext(ProjectContext);
  const token = tokenRetrievalState?.token;

  const isLoggedInUserGC = matchUserProjectRole(
    EUserTypes.GENERAL_CONTRACTOR,
    tokenContents?.role
  );

  const isIntegrationMode = isProjectInIntegrationMode(
    projectDetails ? projectDetails.mode : 0
  );

  const isSubmittalIntgMode =
    featureType === FeatureTypes.SUBMITTALS && isIntegrationMode;

  const { projectParticipants: WFProjectParticipants } = useProjectParticipants(
    isSubmittalIntgMode || !isLoggedInUserGC
  );

  const [mutationUpdateFeatureInstanceFields] = useCIQMutation(
    gql(UPDATE_FEATURE_INSTANCE_BY_PK),
    {
      client: gqlClientForProject
    }
  );

  const [mutationUpdateFeatureInstances] = useCIQMutation(
    gql(UPDATE_FEATURE_INSTANCES),
    {
      client: gqlClientForProject
    }
  );

  const [mutationUpdateFeatureSubmittalFields] = useCIQMutation(
    gql(UPDATE_FEATURE_SUBMITTALS),
    {
      client: gqlClientForProject
    }
  );
  const [mutationUpdateFeatureMaterialFields] = useCIQMutation(
    gql(UPDATE_FEATURE_MATERIALS),
    {
      client: gqlClientForProject
    }
  );

  const [mutationUpdateFeatureSpecSection] = useCIQMutation(
    gql(UPDATE_FEATURE_INSTANCE_SPEC_SECTION),
    {
      client: gqlClientForProject
    }
  );

  const [mutationUpdateFeatureCompanyParticipant] = useCIQMutation(
    gql(UPDATE_FEATURE_COMPANY_PARTICIPANT),
    {
      client: gqlClientForProject
    }
  );

  const [mutationUpdateFeatureCompanyParticipantWithEmptyUsers] =
    useCIQMutation(gql(UPDATE_FEATURE_COMPANY_PARTICIPANT_WITH_EMPTY_USERS), {
      client: gqlClientForProject
    });

  const [mutationUpdateFeatureWorkflowInstanceMilestone] = useCIQMutation(
    gql(UPDATE_FEATURE_WORKFLOW_INSTANCE_MILESTONE),
    {
      client: gqlClientForProject
    }
  );

  const [applyWorkflowMutation] = useCIQMutation(APPLY_WORKFLOW_MUTATION, {
    client: gqlClientForProject
  });

  const [mutationUpdateFeatureUserParticipant] = useCIQMutation(
    gql(MUTATION_UPDATE_FEATURE_USER_PARTICIPANT),
    {
      client: gqlClientForProject
    }
  );

  const [mutationUpdateFeatureBidPackageFields] = useCIQMutation(
    gql(UPDATE_FEATURE_BID_PACKAGE_BY_PK),
    {
      client: gqlClientForProject
    }
  );

  const [mutationUpdateFeatureShippingLocation] = useCIQMutation(
    gql(UPDATE_FEATURE_SHIPPING_LOCATION),
    {
      client: gqlClientForProject
    }
  );

  const [mutationOnDuplicateFeature] = useCIQMutation(
    gql(CREATE_DUPLICATE_OF_FEATURE_INSTANCE),
    {
      client: gqlClientForProject
    }
  );

  const [featureInstanceData, setFeatureInstanceData] = useState<any>();

  const getFeatureInstanceData = useCallback(async () => {
    if (!token) {
      return;
    }

    const fetchInstanceData = async () => {
      const featureInstanceDataResp = await getGQLQueryData(
        token,
        FEATURE_INSTANCE_DETAILS_BY_PK,
        {
          featureId
        }
      );
      if (featureInstanceDataResp?.data?.data) {
        setFeatureInstanceData(
          featureInstanceDataResp.data.data.feature_instance_by_pk
        );
      } else {
        message.error(ErrorMessages.SomethingWentWrong);
      }
    };

    await fetchInstanceData();
  }, [featureId, token]);

  const updateFeatureInstanceFields = useCallback(
    async (params: any) => {
      const payload: any = {
        variables: {
          featureId,
          set: params
        }
      };

      const updateResponse = await mutationUpdateFeatureInstanceFields(payload);

      if (updateResponse.success) {
        message.success("Updated successfully.");
        getFeatureInstanceData();
      } else {
        message.error("Failed to update.");
      }
      return updateResponse;
    },
    [featureId, getFeatureInstanceData, mutationUpdateFeatureInstanceFields]
  );

  const updateBidPackageFields = useCallback(
    async (params: any) => {
      const payload: any = {
        variables: {
          featureId,
          set: params
        }
      };
      const updateResponse = await mutationUpdateFeatureBidPackageFields(
        payload
      );

      if (updateResponse.success) {
        message.success("Updated successfully.");
      } else {
        message.error("Failed to update.");
      }
      return updateResponse;
    },
    [featureId, mutationUpdateFeatureBidPackageFields]
  );

  const updateFeatureInstanceSpecSections = useCallback(
    async (params: { ids: Array<string>; bidscope: boolean }) => {
      const { ids, bidscope } = params;
      const objects = ids.map((spec_section_id: string) => ({
        bid_scope: bidscope,
        feature_instance_id: featureId,
        spec_section_id
      }));

      const variables: any = {
        instance_id: featureId,
        objects
      };
      const updateResponse = await mutationUpdateFeatureSpecSection({
        variables
      });

      if (updateResponse.success) {
        message.success("Updated successfully.");
        getFeatureInstanceData();
      } else {
        message.error("Failed to add.");
      }
      return updateResponse;
    },
    [featureId, getFeatureInstanceData, mutationUpdateFeatureSpecSection]
  );

  const updateFeatureCompanyParticipant = useCallback(
    async (params: {
      vendorIds: Array<string>;
      companyParticipantTypeId: CompanyParticipationType;
    }) => {
      const { vendorIds, companyParticipantTypeId } = params;

      const objects = vendorIds.map((id) => ({
        instance_id: featureId,
        company_participation_type_id: companyParticipantTypeId,
        vendor_id: id
      }));
      const variables: any = {
        instance_id: featureId,
        company_participation_type_id: companyParticipantTypeId,
        objects
      };
      const updateResponse = await mutationUpdateFeatureCompanyParticipant({
        variables
      });

      if (updateResponse?.success) {
        message.success("Updated successfully.");
        getFeatureInstanceData();
      } else {
        message.error("Failed to update.");
      }
      return updateResponse;
    },
    [featureId, getFeatureInstanceData, mutationUpdateFeatureCompanyParticipant]
  );

  const updateFeatureCompanyParticipantWithEmptyUsers = useCallback(
    async (params: {
      vendorIds: Array<string>;
      companyParticipantTypeId: CompanyParticipationType;
      userParticipationTypeId: UserParticipationType;
    }) => {
      const { vendorIds, companyParticipantTypeId, userParticipationTypeId } =
        params;

      const objects = vendorIds.map((id) => ({
        instance_id: featureId,
        company_participation_type_id: companyParticipantTypeId,
        vendor_id: id
      }));
      const variables: any = {
        instance_id: featureId,
        company_participation_type_id: companyParticipantTypeId,
        user_participation_type_id: userParticipationTypeId,
        objects
      };
      const updateResponse =
        await mutationUpdateFeatureCompanyParticipantWithEmptyUsers({
          variables
        });

      if (updateResponse?.success) {
        message.success("Updated successfully.");
        getFeatureInstanceData();
      } else {
        message.error("Failed to update.");
      }
      return updateResponse;
    },
    [
      featureId,
      getFeatureInstanceData,
      mutationUpdateFeatureCompanyParticipantWithEmptyUsers
    ]
  );

  const updateFeatureWorkflowInstanceMilestone = useCallback(
    async (params: {
      id: string;
      set: {
        actual_date?: string | null;
        milestone_action?: string | null;
        name?: string | null;
      };
    }) => {
      const payload = {
        variables: params
      };
      const updateResponse =
        await mutationUpdateFeatureWorkflowInstanceMilestone(payload);

      if (updateResponse.success) {
        message.success("Updated successfully.");
        getFeatureInstanceData();
      } else {
        message.error("Failed to update.");
      }
      return updateResponse;
    },
    [getFeatureInstanceData, mutationUpdateFeatureWorkflowInstanceMilestone]
  );

  const updateFeatureWorkflowInstance = useCallback(
    async (params: {
      input: Array<{
        high_risk_threshold?: number;
        low_risk_threshold?: number;
        override_project_threshold?: boolean;
        pre_WF_start_float?: number;
      }>;
    }) => {
      const payload = {
        variables: {
          input: params.input.map((x) => ({
            ...x,
            feature_instance_id: featureInstanceData?.id,
            workflow_instance_id:
              featureInstanceData?.feature_workflow_instance?.id
          })),
          metadata: {
            request_source: "DETAIL_PAGE"
          }
        }
      };
      const updateResponse = await applyWorkflowMutation(payload);

      if (updateResponse.success) {
        message.success("Updated successfully.");
        getFeatureInstanceData();
      } else {
        message.error("Failed to update.");
      }
      return updateResponse;
    },
    [
      applyWorkflowMutation,
      featureInstanceData?.id,
      featureInstanceData?.feature_workflow_instance?.id,
      getFeatureInstanceData
    ]
  );

  const updateFeatureUserParticipant = useCallback(
    async (params: {
      user_id: string;
      user_participation_type_id: UserParticipationType;
    }) => {
      const isPocUser =
        WFProjectParticipants?.allProjectUsersMap[params.user_id]?.type ===
        "poc";

      const payload: any = {
        variables: {
          featureInstanceId: featureId,
          user_participation_type_id: params?.user_participation_type_id,
          object: {
            instance_id: featureId,
            user_id: isPocUser ? undefined : params?.user_id,
            user_unregistered: isPocUser ? params?.user_id : null,
            user_participation_type_id: params?.user_participation_type_id
          }
        }
      };
      const updateResponse = await mutationUpdateFeatureUserParticipant(
        payload
      );
      if (updateResponse.success) {
        message.success("Updated successfully.");
        getFeatureInstanceData();
      } else {
        message.error("Failed to update.");
      }
      return updateResponse;
    },
    [
      WFProjectParticipants?.allProjectUsersMap,
      featureId,
      getFeatureInstanceData,
      mutationUpdateFeatureUserParticipant
    ]
  );

  useEffect(() => {
    if (!token) return;
    getFeatureInstanceData();
  }, [getFeatureInstanceData, token]);

  // Poll the changes and update the respective UI components
  const previousEventLogs = useRef(eventLogs);

  useEffect(() => {
    if (eventLogs?.length && previousEventLogs.current !== eventLogs) {
      // Check if the change event is related to the current bid or design instance
      // if feature_type is bid/design then match the instance id
      // if feature_type is something differnet than the design and bid
      // then check it inside the linked_features
      const conditionForBidAndDesignPackage = (log: TAuditChangeLog) =>
        hasTheFeature(featureId, log, featureId);
      if (eventLogs.some(conditionForBidAndDesignPackage)) {
        getFeatureInstanceData();
      }
    }
    previousEventLogs.current = eventLogs;
  }, [eventLogs, featureId, getFeatureInstanceData]);

  const updateSubmittalFields = useCallback(
    async (params: any) => {
      const payload: any = {
        variables: {
          featureId,
          set: params
        }
      };
      const updateResponse = await mutationUpdateFeatureSubmittalFields(
        payload
      );

      if (updateResponse.success) {
        message.success("Updated successfully.");
      } else {
        message.error("Failed to update.");
      }
      return updateResponse;
    },
    [featureId, mutationUpdateFeatureSubmittalFields]
  );

  const updateMaterialFields = useCallback(
    async (params: any) => {
      const payload: any = {
        variables: {
          featureId,
          set: params
        }
      };
      const updateResponse = await mutationUpdateFeatureMaterialFields(payload);

      if (updateResponse.success) {
        message.success("Updated successfully.");
      } else {
        message.error("Failed to update.");
      }
      return updateResponse;
    },
    [featureId, mutationUpdateFeatureMaterialFields]
  );

  const updateFeatureShippingLocation = useCallback(
    async (params: any) => {
      const payload: any = {
        variables: {
          featureIds: [featureId],
          placeId: params.placeId
        }
      };
      const updateResponse = await mutationUpdateFeatureShippingLocation(
        payload
      );

      if (updateResponse.success) {
        message.success("Updated successfully.");
      } else {
        message.error("Failed to update.");
      }
      return updateResponse;
    },
    [featureId, mutationUpdateFeatureShippingLocation]
  );

  const updateFeatureFields = useCallback(
    async (params: any, selectedFeatureId: any) => {
      const payload: any = {
        variables: {
          featureId: selectedFeatureId,
          set: params
        }
      };

      const updateResponse = await mutationUpdateFeatureInstanceFields(payload);

      if (updateResponse.success) {
        message.success("Updated successfully.");
        getFeatureInstanceData();
      } else {
        message.error("Failed to update.");
      }
      return updateResponse;
    },
    [getFeatureInstanceData, mutationUpdateFeatureInstanceFields]
  );

  const onDuplicateFeature = useCallback(
    async (params: any) => {
      const payload: any = {
        variables: {
          data: params
        }
      };

      const updateResponse = await mutationOnDuplicateFeature(payload);

      if (updateResponse.success) {
        // message.success("Duplicated successfully.");
      } else {
        message.error("Failed to duplicate.");
      }
      return updateResponse;
    },
    [mutationOnDuplicateFeature]
  );

  const updateFeatureInstancesResponse = useCallback(
    async (params: any) => {
      const updateResponse = await mutationUpdateFeatureInstances(params);
      if (updateResponse.data) {
        getFeatureInstanceData();
      }
      return updateResponse;
    },
    [getFeatureInstanceData, mutationUpdateFeatureInstances]
  );

  return {
    featureInstanceData,
    actions: {
      refetchFeatureInstanceData: getFeatureInstanceData,
      updateFeatureInstanceFields,
      updateBidPackageFields,
      updateFeatureInstanceSpecSections,
      updateFeatureUserParticipant,
      updateFeatureCompanyParticipant,
      updateFeatureCompanyParticipantWithEmptyUsers,
      updateFeatureWorkflowInstanceMilestone,
      updateFeatureWorkflowInstance,
      updateSubmittalFields,
      updateMaterialFields,
      updateFeatureShippingLocation,
      updateFeatureFields,
      onDuplicateFeature,
      updateFeatureInstancesResponse
    }
  };
};
